import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Form, Formik, FormikHelpers } from "formik";
import { journalEntrySchema } from "formValidations/JournalEntrySchema";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import {
  useGetRelatedLedgerEntryQuery,
  useAddLedgerEntryMutation,
} from "store/apis/generalLedger";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { JournalEntry } from "types/Models/journalEntry";
import { JournalEntryTransaction } from "./AddJournalEntryModal";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { AddEditEntries } from "./AddEditEntries";
import { JournalEntryFormWrapper } from "./JournalEntryFormWrapper";
import { JournalEntryInfo } from "./JournalEntryInfo";
import { ACCOUNTING_METHODS } from "constants/accountMethod";
import {
  getMaxExistingSerialNumber,
  maybeCopyAccrualToCash,
} from "utils/journalEntryUtils";

export const DuplicateJournalEntryModal = ({
  close,
  isOpen,
  currentRowData,
  ledgerEntryId,
}: { currentRowData: JournalEntry; ledgerEntryId: string } & ModalProps) => {
  const { alertToast, successToast } = useToast();
  const [addLedgerEntry] = useAddLedgerEntryMutation();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();

  const { entry_name, date } = currentRowData || {};

  const {
    data: relatedLedgerEntry,
    isLoading,
    isSuccess,
  } = useGetRelatedLedgerEntryQuery(
    {
      entityId,
      groupId,
      ledger_entry_id: ledgerEntryId!,
    },
    {
      skip: !entityId || !groupId || !ledgerEntryId! || !isOpen,
      refetchOnMountOrArgChange: true,
    }
  );

  const { transactions = [], ledger_entry } = relatedLedgerEntry || {};

  const initialTransactions: JournalEntryTransaction[] = transactions.map(
    ({
      transaction: {
        description,
        amount,
        uuid,
        memo,
        logo,
        merchant_uuid,
        ledger_entry_serial_number,
      },
      cash_category_id,
      accrual_category_id,
      // basis field no longer exists, so we'll check if the transaction has cash_category_id or accrual_category_id
      // For migration purposes, if neither exists, we'll treat it as accrual
    }) => ({
      id: uuid,
      description: description || null,
      // Backend now provides cash_category_id and accrual_category_id directly
      cash_category_id: cash_category_id || "",
      accrual_category_id: accrual_category_id || "",
      entry_type: cash_category_id
        ? ACCOUNTING_METHODS.CASH
        : ACCOUNTING_METHODS.ACCRUAL,
      merchant: merchant_uuid!,
      uuid,
      memo: memo || null,
      logo: logo || null,
      debit: amount < 0 ? Math.abs(amount) : 0,
      credit: amount > 0 ? Math.abs(amount) : 0,
      invoice: null,
      ledger_entry_serial_number: ledger_entry_serial_number,
    })
  );

  const initialValues = {
    transaction_date: date,
    ledger_entry_name: "".concat(entry_name!).concat(" - Duplicate"),
    ledger_type: (ledger_entry as any)?.ledger_type || "",
    other_ledger_type_str: (ledger_entry as any)?.ledger_type_str || "",
    transactions: initialTransactions,
    is_recurring_enabled: ledger_entry?.settings?.is_recurring_enabled || false,
    frequency: ledger_entry?.settings?.frequency,
    end_date: ledger_entry?.settings?.end_date,
    max_recurring_count: ledger_entry?.settings?.max_recurring_count,
    is_cash_same_as_accrual: ledger_entry?.is_cash_same_as_accrual,
    basis: ledger_entry?.basis || ACCOUNTING_METHODS.ACCRUAL,
  };

  const onSubmit = async (
    values: typeof initialValues,
    { resetForm }: FormikHelpers<typeof initialValues>
  ) => {
    const {
      transactions,
      transaction_date,
      ledger_entry_name,
      ledger_type,
      other_ledger_type_str,
      is_cash_same_as_accrual,
      basis,
    } = values;

    const transactionList = maybeCopyAccrualToCash(
      transactions,
      Boolean(is_cash_same_as_accrual)
    ).map(
      ({
        credit,
        debit,
        cash_category_id,
        accrual_category_id,
        description,
        invoice,
        merchant,
      }) => ({
        amount: Math.abs(Number(credit)) || Math.abs(Number(debit)) * -1,
        description: description!,
        invoice_id: invoice?.uuid || null,
        merchant_data_id: merchant!,
        cash_category_id: cash_category_id || null,
        accrual_category_id: accrual_category_id || null,
      })
    );

    try {
      await addLedgerEntry({
        entityId,
        groupId,
        transactions: transactionList,
        transaction_date: dayjs(transaction_date).format(YYYY_MM_DD),
        ledger_entry_name,
        ledger_type: ledger_type || undefined,
        other_ledger_type_str: other_ledger_type_str || undefined,
        is_recurring_enabled: values.is_recurring_enabled,
        max_recurring_count: values.end_date
          ? undefined
          : Number(values.max_recurring_count),
        frequency: values.frequency,
        is_cash_same_as_accrual: Boolean(is_cash_same_as_accrual),
        end_date:
          values.end_date && !values.max_recurring_count
            ? dayjs(values.end_date).format(YYYY_MM_DD)
            : undefined,
        basis,
      }).unwrap();

      successToast({ message: "Journal Entry has been populated!" });
      resetForm();
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnChange
        validationSchema={journalEntrySchema}
      >
        {({ submitForm, isSubmitting, values }) => (
          <Form className="t-m-0 t-w-full">
            <JournalEntryFormWrapper>
              {({
                isValidTransactions,
                isAmountMatch,
                cashAndAccrualMatch,
              }) => (
                <Modal.Content size="xxxl" useCustomOverlay>
                  <Modal.Header>
                    <div className="t-w-full">
                      <Modal.Title>Add Journal Entry</Modal.Title>
                      <Modal.Subtitle>
                        Please make sure the total debit amount matches the
                        total credit amount
                      </Modal.Subtitle>
                    </div>
                    <Modal.Close />
                  </Modal.Header>
                  <Async.Root
                    isLoading={isLoading}
                    isSuccess={isSuccess}
                    isEmpty={!relatedLedgerEntry}
                  >
                    <Async.Empty>
                      <></>
                    </Async.Empty>
                    <Async.Success>
                      <Modal.Body className="t-gap-4 t-flex t-flex-col">
                        <div className="t-flex t-flex-col t-gap-4">
                          <JournalEntryInfo />
                          <AddEditEntries
                            cashAndAccrualMatch={cashAndAccrualMatch}
                            maxSerialNumber={getMaxExistingSerialNumber(
                              initialTransactions
                            )}
                          />
                        </div>
                      </Modal.Body>
                      <Modal.FooterButtonGroup>
                        <Button onClick={close} type="reset">
                          Cancel
                        </Button>

                        <ConditionalToolTip
                          condition={
                            !isAmountMatch && (
                              <span>
                                Total debits and credits
                                <br /> should match
                              </span>
                            )
                          }
                        >
                          <span>
                            <Button
                              customType="primary"
                              type="submit"
                              onClick={submitForm}
                              disabled={!isValidTransactions || isSubmitting}
                              isLoading={isSubmitting}
                            >
                              Add
                            </Button>
                          </span>
                        </ConditionalToolTip>
                      </Modal.FooterButtonGroup>
                    </Async.Success>
                  </Async.Root>
                </Modal.Content>
              )}
            </JournalEntryFormWrapper>
          </Form>
        )}
      </Formik>
    </Modal.Root>
  );
};
